import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import styles from './ExpandableHtmlSection.module.scss';

const defaultCollapseHeight = 120;

const hasMeaningfulHtml = (value?: string | null) => {
  if (!value) return false;
  const text = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/g, ' ')
    .trim();
  return text.length > 0;
};

export interface ExpandableHtmlSectionProps {
  label: React.ReactNode;
  html?: string | null;
  containerRef?: React.RefObject<HTMLElement>;
  collapseHeight?: number;
  emptyValue?: React.ReactNode;

  className?: string;
  labelClassName?: string;
  contentWrapperClassName?: string;
  contentClassName?: string;
  collapsedClassName?: string;
  expandedClassName?: string;
  emptyClassName?: string;
  buttonClassName?: string;
}

export const ExpandableHtmlSection: React.FC<ExpandableHtmlSectionProps> = ({
  label,
  html,
  containerRef,
  collapseHeight = defaultCollapseHeight,
  emptyValue = '-',
  className,
  labelClassName,
  contentWrapperClassName,
  contentClassName,
  collapsedClassName,
  expandedClassName,
  emptyClassName,
  buttonClassName,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const isEmpty = useMemo(() => !hasMeaningfulHtml(html), [html]);

  useEffect(() => {
    const checkContentHeight = () => {
      const height = contentRef.current?.scrollHeight ?? 0;
      setShowButton(height > collapseHeight);
    };

    const frameId = requestAnimationFrame(checkContentHeight);
    window.addEventListener('resize', checkContentHeight);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', checkContentHeight);
    };
  }, [collapseHeight, html]);

  const handleToggle = () => {
    if (isExpanded) {
      containerRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsExpanded(prev => !prev);
  };

  return (
    <div className={clsx(styles.section, className)}>
      <div className={clsx(styles.label, labelClassName)}>{label}</div>

      {isEmpty ? (
        <div className={clsx(styles.empty, emptyClassName)}>{emptyValue}</div>
      ) : (
        <>
          <div className={clsx(styles.contentWrapper, contentWrapperClassName)}>
            <div
              ref={contentRef}
              className={clsx(
                styles.content,
                contentClassName,
                !isExpanded && showButton ? styles.collapsed : '',
                !isExpanded && showButton ? collapsedClassName : '',
                isExpanded && showButton ? expandedClassName ?? styles.expanded : '',
              )}
              dangerouslySetInnerHTML={{ __html: html as string }}
            />
          </div>
          {showButton && (
            <button
              className={clsx(styles.toggleButton, buttonClassName)}
              onClick={handleToggle}
            >
              {isExpanded ? 'View less' : 'View more'}
            </button>
          )}
        </>
      )}
    </div>
  );
};
